import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';

function normalizeVoiceParticipants(presenceState) {
  const byUserId = new Map();

  Object.values(presenceState || {}).forEach((presences) => {
    presences.forEach((presence) => {
      if (!presence?.user_id) return;

      const nextParticipant = {
        userId: presence.user_id,
        displayName: presence.display_name || 'Unknown user',
        joinedAt: presence.joined_at || null,
        isMuted: Boolean(presence.is_muted),
        isSpeaking: Boolean(presence.is_speaking),
        recentlySpoke: Boolean(presence.recently_spoke),
        hasMicrophone: presence.has_microphone !== false,
        isSelf: false,
      };

      const current = byUserId.get(nextParticipant.userId);
      const currentJoinedAt = current?.joinedAt ? Date.parse(current.joinedAt) : 0;
      const nextJoinedAt = nextParticipant.joinedAt ? Date.parse(nextParticipant.joinedAt) : 0;

      if (!current || nextJoinedAt >= currentJoinedAt) {
        byUserId.set(nextParticipant.userId, nextParticipant);
      }
    });
  });

  return Array.from(byUserId.values()).sort((a, b) => {
    if (a.isSpeaking !== b.isSpeaking) return a.isSpeaking ? -1 : 1;
    if (a.recentlySpoke !== b.recentlySpoke) return a.recentlySpoke ? -1 : 1;
    if (a.isMuted !== b.isMuted) return a.isMuted ? 1 : -1;
    return a.displayName.localeCompare(b.displayName);
  });
}

function toPresenceSummary(roomId, presenceState) {
  const participants = normalizeVoiceParticipants(presenceState);

  return {
    roomId,
    count: participants.length,
    participants,
    speakingCount: participants.filter((participant) => participant.isSpeaking).length,
  };
}

export function useVoicePresenceDirectory(roomIds) {
  const [presenceByRoom, setPresenceByRoom] = useState({});
  const roomIdsKey = Array.from(new Set((roomIds || []).filter(Boolean).map((roomId) => String(roomId))))
    .sort((a, b) => a.localeCompare(b))
    .join('|');
  const normalizedRoomIds = useMemo(
    () => (roomIdsKey ? roomIdsKey.split('|') : []),
    [roomIdsKey],
  );

  useEffect(() => {
    const channels = new Map();
    const snapshotTimeouts = [];
    let isCancelled = false;

    setPresenceByRoom((current) => {
      const next = {};
      normalizedRoomIds.forEach((roomId) => {
        next[roomId] = current[roomId] || {
          roomId,
          count: 0,
          participants: [],
          speakingCount: 0,
        };
      });
      return next;
    });

    const updateRoomPresence = (roomId, presenceState) => {
      if (isCancelled) return;

      setPresenceByRoom((current) => ({
        ...current,
        [roomId]: toPresenceSummary(roomId, presenceState),
      }));
    };

    normalizedRoomIds.forEach((roomId) => {
      const scheduleSnapshot = (channel, delays = [0, 150, 500, 1500]) => {
        delays.forEach((delay) => {
          const timeoutId = window.setTimeout(() => {
            updateRoomPresence(roomId, channel.presenceState());
          }, delay);
          snapshotTimeouts.push(timeoutId);
        });
      };

      const channel = supabase
        .channel(`room-voice-presence:${roomId}`)
        .on('presence', { event: 'sync' }, () => {
          updateRoomPresence(roomId, channel.presenceState());
        })
        .on('presence', { event: 'join' }, () => {
          updateRoomPresence(roomId, channel.presenceState());
        })
        .on('presence', { event: 'leave' }, () => {
          updateRoomPresence(roomId, channel.presenceState());
        })
        .subscribe((status) => {
          if (status === 'SUBSCRIBED') {
            scheduleSnapshot(channel);
          }
        });

      channels.set(roomId, channel);
    });

    return () => {
      isCancelled = true;
      snapshotTimeouts.forEach((timeoutId) => window.clearTimeout(timeoutId));
      channels.forEach((channel) => {
        supabase.removeChannel(channel);
      });
    };
  }, [normalizedRoomIds, roomIdsKey]);

  return presenceByRoom;
}
